/**
 * MRP Permissions Hook
 * 
 * Manages role-based access control for Mini-MRP Lite functionality
 * Based on the specification requirements for different user roles
 */

import { useAuthStore } from '@/store/authStore';
import { MRPPermissions } from '@/types/mrpLite';

export function useMRPPermissions(): MRPPermissions {
  const { user } = useAuthStore();
  
  if (!user) {
    return {
      mrp_view: false,
      mrp_planner: false,
      mrp_run: false,
      mrp_accept: false,
      mrp_convert: false,
      mrp_cancel: false,
    };
  }

  const role = user.role;
  
  // Role-based permissions as per specification
  switch (role) {
    case 'super_admin':
    case 'admin':
      return {
        mrp_view: true,
        mrp_planner: true,
        mrp_run: true,
        mrp_accept: true,
        mrp_convert: true,
        mrp_cancel: true,
      };
      
    case 'owner':
      return {
        mrp_view: true,
        mrp_planner: true,
        mrp_run: true,
        mrp_accept: true,
        mrp_convert: true,
        mrp_cancel: true,
      };
      
    case 'production_manager':
      return {
        mrp_view: true,
        mrp_planner: true,
        mrp_run: true,
        mrp_accept: true,
        mrp_convert: true,
        mrp_cancel: true,
      };
      
    case 'procurement_officer':
      return {
        mrp_view: true,
        mrp_planner: false,
        mrp_run: false,
        mrp_accept: true, // Only for PR rows
        mrp_convert: true, // Only PRs
        mrp_cancel: false,
      };
      
    case 'inventory_manager':
      return {
        mrp_view: true,
        mrp_planner: false,
        mrp_run: false,
        mrp_accept: false,
        mrp_convert: false,
        mrp_cancel: false,
      };
      
    case 'worker':
    default:
      return {
        mrp_view: false,
        mrp_planner: false,
        mrp_run: false,
        mrp_accept: false,
        mrp_convert: false,
        mrp_cancel: false,
      };
  }
}

/**
 * Check if user has MRP planner role for top-bar shortcut
 */
export function useHasMRPPlannerRole(): boolean {
  const permissions = useMRPPermissions();
  return permissions.mrp_planner;
}

/**
 * Check if user can access MRP dashboard
 */
export function useCanAccessMRPDashboard(): boolean {
  const permissions = useMRPPermissions();
  return permissions.mrp_view;
}

/**
 * Check if user can run MRP wizard
 */
export function useCanRunMRP(): boolean {
  const permissions = useMRPPermissions();
  return permissions.mrp_run;
}

/**
 * Check if user can accept MRP suggestions
 */
export function useCanAcceptSuggestions(): boolean {
  const permissions = useMRPPermissions();
  return permissions.mrp_accept;
}

/**
 * Check if user can convert planned orders
 */
export function useCanConvertPlannedOrders(): boolean {
  const permissions = useMRPPermissions();
  return permissions.mrp_convert;
}

/**
 * Check if user can cancel planned orders
 */
export function useCanCancelPlannedOrders(): boolean {
  const permissions = useMRPPermissions();
  return permissions.mrp_cancel;
}
